import {
  hintsAdminControllerCreateOne,
  hintsAdminControllerDeleteOne,
  hintsAdminControllerList,
  hintsAdminControllerUpdateOne,
  type CreateHint,
  type Hint,
  type UpdateHint,
  unwrapApiResponse,
} from "@cryptors/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const getHints = async (cryptorId: number): Promise<Hint[]> => {
  const filter = `cryptorId||$eq||${cryptorId}`;
  const data = await unwrapApiResponse(
    hintsAdminControllerList<true>({
      query: { filter: [filter], limit: 200 },
      throwOnError: true,
    }),
  );
  return Array.isArray(data) ? data : (data as { data: Hint[] }).data;
};

export const useGetHints = (cryptorId: number) =>
  useQuery({
    initialData: [],
    queryKey: ["hints", cryptorId],
    queryFn: () => getHints(cryptorId),
  });

const postHint = async (data: CreateHint) => {
  return unwrapApiResponse(
    hintsAdminControllerCreateOne<true>({
      body: data,
      throwOnError: true,
    }),
  );
};

export const usePostHint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postHint,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hints"] });
    },
  });
};

const patchHint = async ({ id, data }: { id: string; data: UpdateHint }) => {
  return unwrapApiResponse(
    hintsAdminControllerUpdateOne<true>({
      body: data,
      path: { id },
      throwOnError: true,
    }),
  );
};

export const usePatchHint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchHint,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hints"] });
    },
  });
};

const deleteHint = async (id: string) => {
  return unwrapApiResponse(
    hintsAdminControllerDeleteOne<true>({
      path: { id },
      throwOnError: true,
    }),
  );
};

export const useDeleteHint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHint,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hints"] });
    },
  });
};
